import { useState } from "react";

import { Box, Button, ButtonProps, Tooltip } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import _ from "lodash";
import Image from "next/image";
import { GoogleLogin, GoogleLoginResponse } from "react-google-login";

import GoogleLogo from "../../assets/google.svg";

interface GoogleSignInProps {
  buttonText?: string;
  disabledOverride?: boolean;
  onLoginSuccess?: () => void;
  buttonProps?: Omit<ButtonProps, "children">;
  source?: "benchmarking";
}

export function GoogleSignIn({
  buttonText = "Login with Google",
  disabledOverride = false,
  onLoginSuccess = _.noop,
  buttonProps,
  source,
}: GoogleSignInProps): JSX.Element {
  const [disabled, setDisabled] = useState(false);

  const linkGoogleAccount = (response: GoogleLoginResponse) => {
    return fetch("/api/v1/auth/login/federated/google", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: response.tokenId,
        source,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        onLoginSuccess();
      })
      .catch((err) => {
        showNotification({
          color: "red",
          title: "Error",
          message: "We weren't able to link your account.",
        });

        console.error(err);
      });
  };

  const tooltipLabel = disabledOverride
    ? "Please agree to the Privacy Policy to continue."
    : disabled
      ? "Google sign-in is disabled."
      : undefined;

  // we need both of these because only NEXT_PUBLIC_ variables are available on the nextjs part of the client
  // when we consolidate the client into one frontend, we can remove the REACT_APP_ variables
  const clientId =
    process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? process.env.REACT_APP_GOOGLE_CLIENT_ID ?? "";

  return (
    <>
      {navigator.cookieEnabled && (
        <Tooltip label={tooltipLabel} disabled={!tooltipLabel} position="top-end">
          <Box
            sx={{
              width: "100%",
              cursor: disabled || disabledOverride ? "not-allowed" : "pointer",
            }}
          >
            <Box
              sx={{
                pointerEvents: disabled || disabledOverride ? "none" : "auto",
              }}
            >
              <GoogleLogin
                disabled={disabled || disabledOverride}
                clientId={clientId}
                onSuccess={(response) => linkGoogleAccount(response as GoogleLoginResponse)}
                onFailure={(error: unknown) => {
                  console.error(error);
                  showNotification({
                    color: "red",
                    title: "Error",
                    message: "Something went wrong. Please try again.",
                  });
                }}
                onScriptLoadFailure={(error) => {
                  console.log("script load failure", error);
                  return setDisabled(true);
                }}
                render={(renderProps) => (
                  <Button
                    size="lg"
                    {...buttonProps}
                    {...renderProps}
                    sx={{ width: "100%" }}
                    data-heap-id="google-sign-in-button"
                    variant="tertiary"
                    leftSection={<Image {...GoogleLogo} width={18} height={18} alt="google" />}
                  >
                    {buttonText}
                  </Button>
                )}
              />
            </Box>
          </Box>
        </Tooltip>
      )}
    </>
  );
}
